import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../ui/card";
import { User, UserCheck, MailCheck, Shield, Search, X } from "lucide-react";
import { Input } from "../../ui/input";
import { supabase } from "../../../lib/supabase";

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

interface UserStatsProps {
  apiPrefix: 'admin' | 'developer';
  onStatsLoad?: (stats: any) => void;
}

export function UserStats({ apiPrefix, onStatsLoad }: UserStatsProps) {
  const [referralCodes, setReferralCodes] = useState<any[]>([]);
  const [codeSearch, setCodeSearch] = useState('');
  const [selectedCode, setSelectedCode] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const fetchCodes = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_URL}/api/referral`, {
          headers: { 'Authorization': `Bearer ${session?.access_token}` }
        });
        const json = await res.json();
        if (json.ok) setReferralCodes(json.data);
      } catch (e) {}
    };
    fetchCodes();
  }, []);

  const [userStats, setUserStats] = useState({
    registered: 0,
    verified: 0,
    freeTrialUsers: 0,
    active: 0,
    subscriber: 0,
    admins: 0,
    developers: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadUserStats = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      const url = new URL(`${API_URL}/api/${apiPrefix}/stats`);
      if (selectedCode) url.searchParams.append("referralCode", selectedCode);
      
      const response = await fetch(url.toString(), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load user stats');
      }

      const json = await response.json();
      
      if (json.ok && json.data) {
        const data = json.data;
        
        const stats = {
          registered: data.registered || 0,
          verified: data.verified || 0,
          freeTrialUsers: data.freeTrialUsers || 0,
          active: data.active || 0,
          subscriber: data.subscriber || 0,
          admins: data.admins || 0,
          developers: data.developers || 0
        };

        setUserStats(stats);
        onStatsLoad?.(stats);
      }
    } catch (err) {
      console.error('Error loading user stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserStats();
  }, [selectedCode]);

  const getPercentage = (value: number) => {
    if (isLoading || !userStats.registered) return null;
    const pct = Math.round((value / userStats.registered) * 100);
    return <div className="absolute bottom-3 right-4 text-[10px] text-muted-foreground whitespace-nowrap">{pct}% of total</div>;
  };

  const filteredCodes = referralCodes.filter(c => c.code.toLowerCase().includes(codeSearch.toLowerCase()));

  return (
    <Card className="mb-8 border-slate-800 bg-background/50 backdrop-blur">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <CardTitle>Account Summary</CardTitle>
          <CardDescription>Overview of user statistics and subscriptions.</CardDescription>
        </div>
        
        <div className="relative w-full sm:w-72">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Filter by Referral Code..." 
              className="pl-9 pr-9"
              value={codeSearch}
              onChange={(e) => {
                setCodeSearch(e.target.value.toUpperCase());
                setShowDropdown(true);
                if (selectedCode && e.target.value === '') {
                  setSelectedCode('');
                }
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            />
            {selectedCode && (
              <button 
                className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setSelectedCode('');
                  setCodeSearch('');
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {showDropdown && (
            <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
              {filteredCodes.length > 0 ? (
                filteredCodes.map(c => (
                  <div 
                    key={c.id} 
                    className="px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer flex justify-between items-center"
                    onMouseDown={() => {
                      setSelectedCode(c.code);
                      setCodeSearch(c.code);
                      setShowDropdown(false);
                    }}
                  >
                    <span className="font-bold text-blue-500">{c.code}</span>
                    <span className="text-xs text-muted-foreground">{c.owner?.full_name || 'No Owner'}</span>
                  </div>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">No codes found.</div>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
      <Card className="relative">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium">Registered Account</CardTitle>
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent className="pb-6">
          <div className="text-xl font-bold">{isLoading ? <span className="text-muted-foreground text-xs animate-pulse">Loading...</span> : userStats.registered}</div>
          {getPercentage(userStats.registered)}
        </CardContent>
      </Card>
      
      <Card className="relative">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium">Verified</CardTitle>
          <MailCheck className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent className="pb-6">
          <div className="text-xl font-bold text-blue-600">{isLoading ? <span className="text-muted-foreground text-xs animate-pulse">Loading...</span> : userStats.verified}</div>
          {getPercentage(userStats.verified)}
        </CardContent>
      </Card>

      <Card className="relative">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium">Free Trial Used</CardTitle>
          <UserCheck className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent className="pb-6">
          <div className="text-xl font-bold text-orange-600">{isLoading ? <span className="text-muted-foreground text-xs animate-pulse">Loading...</span> : userStats.freeTrialUsers}</div>
          {getPercentage(userStats.freeTrialUsers)}
        </CardContent>
      </Card>
      
      <Card className="relative">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium truncate" title="Active (Free Trial + Subscriber)">Active <span className="hidden xl:inline">(Trial+Sub)</span></CardTitle>
          <UserCheck className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent className="pb-6">
          <div className="text-xl font-bold text-green-600">{isLoading ? <span className="text-muted-foreground text-xs animate-pulse">Loading...</span> : userStats.active}</div>
          {getPercentage(userStats.active)}
        </CardContent>
      </Card>

      <Card className="relative">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium">Subscriber</CardTitle>
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent className="pb-6">
          <div className="text-xl font-bold text-indigo-600">{isLoading ? <span className="text-muted-foreground text-xs animate-pulse">Loading...</span> : userStats.subscriber}</div>
          {getPercentage(userStats.subscriber)}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium">{apiPrefix === 'admin' ? 'Admins' : 'Developers'}</CardTitle>
          <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="text-xl font-bold text-purple-600">
            {isLoading ? <span className="text-muted-foreground text-xs animate-pulse">Loading...</span> : (apiPrefix === 'admin' ? userStats.admins : userStats.developers)}
          </div>
        </CardContent>
      </Card>
        </div>
      </CardContent>
    </Card>
  );
}

